import { useCallback, useEffect, useMemo, useRef, useState, type RefObject } from 'react';
import type { IMatrixColumn, IProgressMatrix } from './progressMatrix';
import type { ITrackerPopulation } from './useTrackerPopulation';

/**
 * `columns` in the order the user saw them (`order`, column ids). A column the
 * held order doesn't know — one a refetch brought in — goes after the held
 * ones, in its built order (the sort is stable).
 */
export function inHeldOrder(columns: readonly IMatrixColumn[], order: readonly string[]): IMatrixColumn[] {
  const rank = new Map(order.map((id, index) => [id, index] as const));
  const rankOf = (column: IMatrixColumn) => rank.get(column.taskId) ?? order.length;
  return [...columns].sort((a, b) => rankOf(a) - rankOf(b));
}

export interface ITrackerLayoutHold {
  /** The matrix to render: `built`, in the held column order while held. */
  matrix: IProgressMatrix;
  /** The grid's zone — working inside it holds the layout, leaving it releases. */
  zoneRef: RefObject<HTMLDivElement | null>;
  /** The user started working the grid (a pointer press or a key inside it). */
  begin: () => void;
}

/**
 * Keeps the tracker's columns where they are while the user works the grid
 * (QA F-61). A tap re-sorts a "% complete" / "On-time" order and can drop a
 * column under "Hide 100 %" or a Status filter, so the next tap would land on
 * the neighbour. From the first press inside the grid, the column ORDER is held
 * here and the column SET by the population (`hold`); values stay live. The
 * layout is re-applied when the user's attention leaves the grid: a press or a
 * focus move anywhere outside it, or a mouse/pen pointer leaving it (a touch
 * pointer "leaves" after every tap, so touch releases on the next press
 * outside). A different level, sort or direction (`viewKey`) is a new layout,
 * never shown in an old order.
 */
export function useTrackerLayoutHold(
  population: Pick<ITrackerPopulation, 'hold' | 'release'>,
  built: IProgressMatrix,
  viewKey: string,
): ITrackerLayoutHold {
  const zoneRef = useRef<HTMLDivElement | null>(null);
  const [order, setOrder] = useState<{ viewKey: string; ids: string[] } | null>(null);
  const { hold, release } = population;

  const begin = useCallback(() => {
    hold();
    setOrder((held) => (held?.viewKey === viewKey ? held : { viewKey, ids: built.columns.map((c) => c.taskId) }));
  }, [hold, viewKey, built]);

  const held = order !== null;
  useEffect(() => {
    if (!held) return;
    const zone = zoneRef.current!; // `begin` only fires from inside it
    const end = () => {
      release();
      setOrder(null);
    };
    const outside = (event: Event) => {
      if (!zone.contains(event.target as Node)) end();
    };
    const leave = (event: PointerEvent) => {
      if (event.pointerType !== 'touch') end();
    };
    document.addEventListener('pointerdown', outside, true);
    document.addEventListener('focusin', outside);
    zone.addEventListener('pointerleave', leave);
    return () => {
      document.removeEventListener('pointerdown', outside, true);
      document.removeEventListener('focusin', outside);
      zone.removeEventListener('pointerleave', leave);
    };
  }, [held, release]);

  const matrix = useMemo(
    () => (order?.viewKey === viewKey ? { ...built, columns: inHeldOrder(built.columns, order.ids) } : built),
    [built, order, viewKey],
  );
  return { matrix, zoneRef, begin };
}
